import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ChevronLeft, Heart, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { Sparkle } from "../components/BrandLogo";
import HonorWall from "../components/HonorWall";
import { toast } from "sonner";

const ONE_TIME = [
  { id: "d5",  label: "$5",  hint: "A small gift" },
  { id: "d15", label: "$15", hint: "Most chosen" },
  { id: "d30", label: "$30", hint: "Deep care" },
];
const MONTHLY = [
  { id: "m5",  label: "$5/mo",  hint: "A quiet patron" },
  { id: "m10", label: "$10/mo", hint: "A steady hand" },
  { id: "m25", label: "$25/mo", hint: "A guardian gift" },
];

export default function Donate() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [mode, setMode] = useState("one_time"); // one_time | monthly
  const [preset, setPreset] = useState("d15");
  const [custom, setCustom] = useState("");
  const [email, setEmail] = useState("");
  const [showDedication, setShowDedication] = useState(false);
  const [honoree, setHonoree] = useState("");
  const [dedication, setDedication] = useState("");
  const [donorLabel, setDonorLabel] = useState("");
  const [sharePublicly, setSharePublicly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  // Handle return-from-Stripe success
  useEffect(() => {
    const params = new URLSearchParams(search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    setStatus({ kind: "pending" });
    const tick = async () => {
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({
            kind: "paid",
            amount: data.amount,
            mode: data.mode,
            honoree_name: data.honoree_name,
            dedication_note: data.dedication_note,
            share_publicly: data.share_publicly,
          });
          return;
        }
        if (data.status === "expired") {
          setStatus({ kind: "expired" });
          return;
        }
      } catch { /* ignore */ }
      attempts++;
      if (attempts < 6) setTimeout(tick, 2000);
      else setStatus({ kind: "pending_long" });
    };
    tick();
    return () => { cancelled = true; };
  }, [search]);

  const presets = mode === "one_time" ? ONE_TIME : MONTHLY;

  const go = async () => {
    setBusy(true);
    try {
      const body = {
        mode,
        origin_url: window.location.origin,
        email: email || undefined,
      };
      if (custom) body.custom_amount = parseFloat(custom);
      else body.preset = preset;
      if (showDedication && honoree.trim()) {
        body.honoree_name = honoree.trim().slice(0, 80);
        if (dedication.trim()) body.dedication_note = dedication.trim().slice(0, 280);
        if (donorLabel.trim()) body.donor_label = donorLabel.trim().slice(0, 40);
        body.share_publicly = sharePublicly;
      }
      const { data } = await api.post("/donations/checkout", body);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn’t open checkout.");
    } finally {
      setBusy(false);
    }
  };

  if (status?.kind === "paid") {
    return (
      <AppShell>
        <div className="px-8 pt-24 text-center" data-testid="donate-success">
          <div className="mx-auto w-16 h-16 rounded-full bg-brand/10 text-brand grid place-items-center">
            <Heart className="w-7 h-7" />
          </div>
          <p className="font-script text-2xl text-brand mt-4">thank you, truly</p>
          <h1 className="font-serif text-4xl leading-tight mt-1">
            Your <em>{status.mode === "monthly" ? "first month of support" : "gift"}</em> is held with care.
          </h1>
          <p className="mt-3 text-foreground/70 leading-relaxed">
            ${status.amount?.toFixed(2)} — this helps keep Sister Signal free for women who need it.
          </p>

          {status.honoree_name && (
            <div
              data-testid="donate-success-dedication"
              className="mt-7 mx-auto max-w-sm rounded-3xl bg-card border border-border p-6 text-left -rotate-[0.5deg] shadow-[0_8px_24px_-16px_rgba(157,31,90,0.25)]"
            >
              <p className="font-script text-xl text-brand">in honor of</p>
              <p className="font-serif text-2xl italic mt-1 leading-snug text-foreground">
                {status.honoree_name}
              </p>
              {status.dedication_note && (
                <p className="mt-3 font-serif italic text-[15px] leading-relaxed text-foreground/85">
                  “{status.dedication_note}”
                </p>
              )}
              <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-foreground/45">
                {status.share_publicly
                  ? "Quietly added to the wall."
                  : "Kept private, between us."}
              </p>
            </div>
          )}

          {status.mode === "monthly" && (
            <p className="mt-3 text-xs text-foreground/55 leading-relaxed max-w-xs mx-auto">
              Note: recurring billing will activate when our production payment account is connected. You will not be charged again automatically until then.
            </p>
          )}
          <Button data-testid="donate-back-home" onClick={() => navigate("/today")} className="mt-8 rounded-full bg-brand hover:bg-brand/90 text-white h-12 px-8">
            Back to today
          </Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-donate">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <p className="font-script text-2xl text-brand">support the signal</p>
        <h1 className="font-serif text-4xl leading-tight">A small gift, <em>quietly</em>.</h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">
          Sister Signal is survivor-informed and community-supported. Your gift keeps this work free for women who need it.
        </p>
      </div>

      {status?.kind === "expired" && (
        <div className="mx-7 mb-4 rounded-2xl bg-card border border-border p-4 text-sm" data-testid="donate-expired">
          That checkout expired. No charge was made. You can try again below.
        </div>
      )}

      <section className="px-8">
        <div className="flex bg-card border border-border rounded-full p-1 mb-5">
          <button
            data-testid="donate-mode-onetime"
            onClick={() => { setMode("one_time"); setPreset("d15"); setCustom(""); }}
            className={`flex-1 h-10 rounded-full text-sm font-medium transition-all ${mode === "one_time" ? "bg-brand text-white shadow-sm" : "text-foreground/70"}`}
          >
            One-time gift
          </button>
          <button
            data-testid="donate-mode-monthly"
            onClick={() => { setMode("monthly"); setPreset("m10"); setCustom(""); }}
            className={`flex-1 h-10 rounded-full text-sm font-medium transition-all ${mode === "monthly" ? "bg-brand text-white shadow-sm" : "text-foreground/70"}`}
          >
            Monthly supporter
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {presets.map((p) => (
            <button
              key={p.id}
              data-testid={`donate-preset-${p.id}`}
              onClick={() => { setPreset(p.id); setCustom(""); }}
              className={`relative rounded-3xl border p-4 text-left transition-all ${
                preset === p.id && !custom ? "border-brand bg-brand/8 shadow-sm" : "border-border bg-card hover:border-brand/40"
              }`}
            >
              {p.id === "d15" && !custom && (
                <Sparkle className="absolute top-2 right-2 w-3 h-3 text-brand" />
              )}
              <div className="font-serif text-2xl text-foreground">{p.label}</div>
              <div className="text-xs text-foreground/60 mt-1">{p.hint}</div>
            </button>
          ))}
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-foreground/55">Or a custom amount</Label>
          <div className="relative mt-1">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-foreground/60">$</span>
            <Input
              data-testid="donate-custom-input"
              value={custom}
              onChange={(e) => { setCustom(e.target.value.replace(/[^0-9.]/g, "")); setPreset(null); }}
              placeholder={mode === "monthly" ? "Monthly amount" : "Any amount"}
              className="h-13 rounded-2xl pl-9"
            />
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-foreground/55">Email (optional, for receipt)</Label>
          <Input
            data-testid="donate-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="you@example.com"
            className="mt-1 h-13 rounded-2xl"
          />
        </div>

        <Button
          data-testid="donate-submit-btn"
          onClick={go}
          disabled={busy}
          className="mt-6 w-full h-14 rounded-full bg-brand hover:bg-brand/90 text-white text-base"
        >
          {busy ? "Opening secure checkout…" : mode === "monthly" ? "Become a monthly supporter" : "Send a gift"}
          <Sparkles className="w-4 h-4 ml-2" />
        </Button>
        <p className="mt-3 text-center text-xs text-foreground/55">
          Secure checkout by Stripe. Cancel a monthly gift any time.
        </p>

        {/* Dedication — quietly optional */}
        <div className="mt-7">
          {!showDedication ? (
            <button
              data-testid="donate-add-dedication"
              onClick={() => setShowDedication(true)}
              className="w-full rounded-3xl bg-card border border-dashed border-border p-5 text-left hover:border-brand/40 transition-all"
            >
              <div className="flex items-center gap-2">
                <Sparkle className="w-3 h-3 text-brand" />
                <span className="font-script text-xl text-brand">give in honor of someone</span>
              </div>
              <p className="mt-1 text-sm text-foreground/65 leading-relaxed">
                Optional. A quiet note, kept private — or, if you choose, shared on the wall.
              </p>
            </button>
          ) : (
            <div className="rounded-3xl bg-card border border-border p-5" data-testid="dedication-form">
              <div className="flex items-center justify-between">
                <p className="font-script text-xl text-brand">in honor of</p>
                <button
                  data-testid="dedication-clear"
                  onClick={() => {
                    setShowDedication(false);
                    setHonoree(""); setDedication(""); setDonorLabel(""); setSharePublicly(false);
                  }}
                  className="text-xs text-foreground/55 hover:text-brand"
                >
                  remove
                </button>
              </div>
              <Label className="text-xs uppercase tracking-wider text-foreground/55 mt-3 block">Their name (or how you’d like to remember them)</Label>
              <Input
                data-testid="dedication-honoree-input"
                value={honoree}
                onChange={(e) => setHonoree(e.target.value)}
                placeholder="my mother · my sister · the woman I used to be"
                maxLength={80}
                className="mt-1 h-12 rounded-2xl"
              />
              <Label className="text-xs uppercase tracking-wider text-foreground/55 mt-4 block">A private note (optional)</Label>
              <Textarea
                data-testid="dedication-note-input"
                value={dedication}
                onChange={(e) => setDedication(e.target.value)}
                placeholder="Say what you would say."
                maxLength={280}
                className="mt-1 min-h-24 rounded-2xl"
              />
              <p className="mt-1 text-[11px] text-foreground/50 text-right">{dedication.length}/280</p>

              <label className="mt-3 flex items-start gap-3 cursor-pointer" data-testid="dedication-share-toggle">
                <input
                  type="checkbox"
                  checked={sharePublicly}
                  onChange={(e) => setSharePublicly(e.target.checked)}
                  className="mt-1 accent-[hsl(var(--brand))] w-4 h-4"
                />
                <span className="text-sm text-foreground/80 leading-snug">
                  Add this honor to the quiet wall.{" "}
                  <span className="text-foreground/55">No amount is ever shown. You can stay anonymous below.</span>
                </span>
              </label>

              {sharePublicly && (
                <>
                  <Label className="text-xs uppercase tracking-wider text-foreground/55 mt-4 block">How to sign it (optional)</Label>
                  <Input
                    data-testid="dedication-donor-input"
                    value={donorLabel}
                    onChange={(e) => setDonorLabel(e.target.value)}
                    placeholder="from M.   ·   from her older sister   ·   from a friend"
                    maxLength={40}
                    className="mt-1 h-12 rounded-2xl"
                  />
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="px-8 mt-12 pb-10">
        <HonorWall />
      </section>

      {status?.kind === "pending_long" && (
        <div className="mx-7 mt-6 rounded-2xl bg-card border border-border p-4 text-sm" data-testid="donate-pending">
          We’re still confirming your last gift. If the page doesn’t update soon, check your email.
        </div>
      )}

      <div className="h-10" />
    </AppShell>
  );
}
